import React, { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { useAdminAuth } from "../AuthContext";

const ROLE_LABELS = { admin: "Administrator", medarbejder: "Medarbejder", viewer: "Læseadgang" };

const Users = () => {
  const { user } = useAdminAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", name: "", role: "medarbejder", temp_password: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.users().then(setUsers).catch(() => setError("Kunne ikke hente brugere."));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setMsg("");
    if (form.temp_password.length < 12) return setError("Midlertidig adgangskode skal være mindst 12 tegn.");
    setBusy(true);
    try {
      await api.createUser({ ...form, email: form.email.trim().toLowerCase() });
      setMsg("Bruger oprettet. De skal skifte adgangskode ved første login.");
      setForm({ email: "", name: "", role: "medarbejder", temp_password: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.detail) || "Kunne ikke oprette bruger.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (u) => {
    try {
      await api.setUserActive(u.id, !u.active);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.detail) || "Kunne ikke opdatere bruger.");
    }
  };

  return (
    <div data-testid="admin-users-page" className="max-w-4xl">
      <h1 className="font-display text-3xl text-[#1A2E24] mb-1">Brugere</h1>
      <p className="text-sm text-[#5C7567] mb-6">Opret og deaktivér adgang. Nye brugere tvinges til at skifte adgangskode.</p>

      <form onSubmit={create} className="rounded-2xl bg-white border border-black/5 p-6 mb-8" data-testid="admin-users-form">
        <h2 className="font-display text-lg text-[#1A2E24] mb-4 flex items-center gap-2"><UserPlus className="h-5 w-5" /> Opret bruger</h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <input required type="email" placeholder="E-mail" value={form.email} data-testid="admin-users-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-xl border border-black/10 px-3 py-2.5" />
          <input required placeholder="Navn" value={form.name} data-testid="admin-users-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-xl border border-black/10 px-3 py-2.5" />
          <select value={form.role} data-testid="admin-users-role"
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="rounded-xl border border-black/10 px-3 py-2.5 bg-white">
            {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input required type="text" placeholder="Midlertidig adgangskode (min. 12 tegn)" value={form.temp_password}
            data-testid="admin-users-temppw"
            onChange={(e) => setForm({ ...form, temp_password: e.target.value })}
            className="rounded-xl border border-black/10 px-3 py-2.5" />
        </div>
        {error && <p className="text-sm text-red-600 mb-3" data-testid="admin-users-error">{error}</p>}
        {msg && <p className="text-sm text-green-700 mb-3" data-testid="admin-users-msg">{msg}</p>}
        <button type="submit" disabled={busy} data-testid="admin-users-submit"
          className="inline-flex items-center gap-2 rounded-full bg-[#2D5A3F] text-white px-5 py-2.5 text-sm hover:bg-[#244b34] transition-colors duration-200 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Opret bruger
        </button>
      </form>

      {!users ? (
        <div className="flex items-center gap-2 text-[#5C7567]"><Loader2 className="h-5 w-5 animate-spin" /> Henter…</div>
      ) : (
        <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-[#5C7567] border-b border-black/5">
                <th className="px-4 py-3 font-medium">Navn</th>
                <th className="px-4 py-3 font-medium">E-mail</th>
                <th className="px-4 py-3 font-medium">Rolle</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-black/5 last:border-0" data-testid={`admin-users-row-${u.email}`}>
                  <td className="px-4 py-3 text-[#1A2E24]">{u.name}</td>
                  <td className="px-4 py-3 text-[#5C7567]">{u.email}</td>
                  <td className="px-4 py-3">{ROLE_LABELS[u.role] || u.role}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs ${u.active ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`}>
                      {u.active ? "Aktiv" : "Deaktiveret"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.email !== user?.email && (
                      <button onClick={() => toggle(u)} data-testid={`admin-users-toggle-${u.email}`}
                        className="text-sm text-[#2D5A3F] hover:underline">
                        {u.active ? "Deaktivér" : "Aktivér"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Users;
